// Ponto ÚNICO de registro de erros da interface.
//
// Hoje escreve um objeto estruturado em `console.error` (uma linha JSON com o
// prefixo "[ui-error]", fácil de filtrar no DevTools e de coletar por quem
// monitora o navegador). Quando houver um serviço de coleta (Sentry, endpoint
// próprio...), basta trocar `sink` aqui: nenhum outro arquivo precisa mudar.
//
// O que NÃO entra no registro, de propósito: token, conteúdo do localStorage,
// querystring da URL, dados de sentenciados/vítimas. Só o id do operador, a
// rota (sem query), o tipo/mensagem do erro e as primeiras linhas da pilha.

const MAX_STACK_LINES = 8;

// Erro de download de pedaço de código (chunk) - acontece quando uma nova
// versão é publicada com a aba antiga aberta, ou a rede cai no meio.
export const isChunkLoadError = (error) => {
  const text = `${(error && error.name) || ''} ${
    (error && error.message) || ''
  }`;
  return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/i.test(
    text
  );
};

export const classifyError = (error) => {
  if (isChunkLoadError(error)) return 'chunk';
  const text = `${(error && error.name) || ''} ${
    (error && error.message) || ''
  }`;
  if (/Network Error|Failed to fetch|NetworkError/i.test(text))
    return 'network';
  return 'render';
};

const newErrorId = () =>
  `ERR-${Date.now().toString(36).toUpperCase()}${Math.random()
    .toString(36)
    .slice(2, 6)
    .toUpperCase()}`;

const currentUserId = () => {
  try {
    const raw = window.localStorage.getItem('current_user');
    const user = raw ? JSON.parse(raw) : null;
    return user && user.id != null ? user.id : null;
  } catch (e) {
    return null;
  }
};

export const buildErrorRecord = (error, context = {}) => {
  const stack =
    error && typeof error.stack === 'string'
      ? error.stack.split('\n').slice(0, MAX_STACK_LINES).join('\n')
      : null;
  return {
    evt: 'ui_error',
    id: newErrorId(),
    kind: classifyError(error),
    source: context.source || 'unknown',
    name: (error && error.name) || 'Error',
    message: String((error && error.message) || error || 'erro desconhecido'),
    stack,
    componentStack: context.componentStack
      ? String(context.componentStack)
          .split('\n')
          .slice(0, MAX_STACK_LINES)
          .join('\n')
      : null,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    userId: currentUserId(),
    at: new Date().toISOString(),
  };
};

// Destino dos registros. Substituível em testes e, no futuro, por um coletor.
let sink = (record) => {
  // eslint-disable-next-line no-console
  console.error('[ui-error]', JSON.stringify(record));
};

export const setErrorSink = (newSink) => {
  sink = newSink;
};

/**
 * Registra um erro e devolve o registro (o `id` pode ser mostrado ao operador
 * para ele informar ao suporte). Nunca lança.
 */
export const reportError = (error, context = {}) => {
  const record = buildErrorRecord(error, context);
  try {
    sink(record);
  } catch (e) {
    // registrar não pode causar outro erro
  }
  return record;
};

let installed = false;
/** Registra também os erros que escapam de qualquer boundary. Idempotente. */
export const installGlobalErrorHandlers = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => {
    reportError(event.error || new Error(event.message), {
      source: 'window.onerror',
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const { reason } = event;
    reportError(reason instanceof Error ? reason : new Error(String(reason)), {
      source: 'unhandledrejection',
    });
  });
};
